import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

// api
import { authSMS, checkAuthSMS, checkExistUser, getUserID } from "../../services/apis/auth";

// components
import CommonModal from "../../components/modal/CommonModal";

// utils
import { hasForbiddenChars, FORBIDDEN_CHARS_MSG } from "../../utils/sanitize";

function SearchAccount({type}) {
    const lastPhoneNumInputRef = useRef(null);
    const authRevision = useRef(0);
    const [checkingAuth, setCheckingAuth] = useState(false);
    const [id, setId] = useState("");
    const [displayName, setDisplayName] = useState("");
    const [phoneNum, setPhoneNum] = useState(type==='dtx006' ? { first: "010", middle: "", last: ""} : { first: "", middle: "", last: ""});
    const [authNum, setAuthNum] = useState("");

    const [displayUserID, setDisplayUserID] = useState("");

    // none: 미전송, send: 전송, fail: 전송실패
    const [sendAuthState, setSendAuthState] = useState("none");
    // none: 인증번호 미입력, success: 인증번호 일치, fail: 인증번호 불일치
    const [isValidAuth, setIsValidAuth] = useState("none");

    const [tab, setTab] = useState('id');

    const [showNotUserModal, setShowNotUserModal] = useState(false);
    const [alertMessage, setAlertMessage] = useState("회원이 아닙니다.");

    const navigate = useNavigate();

    const resetVerification = () => {
        authRevision.current += 1;
        setCheckingAuth(false);
        setSendAuthState("none");
        setAuthNum("");
        setIsValidAuth("none");
        setDisplayUserID("");
    };

    const initializeInfo = () => {
        resetVerification();
        setId("");
        setDisplayName("");
        setPhoneNum(type==='dtx006' ? { first: "010", middle: "", last: ""} : { first: "", middle: "", last: ""});
        setAuthNum("");
        setSendAuthState("none");
        setIsValidAuth("none");
        setDisplayUserID("");
    }

    const handleSendAuthSMS = async () => {
        if(hasForbiddenChars(displayName) || hasForbiddenChars(phoneNum.first) || hasForbiddenChars(phoneNum.middle) || hasForbiddenChars(phoneNum.last)) {
            return;
        }
        if(tab==="pw" && !id) {
            setAlertMessage("아이디를 입력해주세요.");
            setShowNotUserModal(true);
            return;
        }
        if (!displayName.trim()) {
            setAlertMessage("이름을 입력해주세요.");
            setShowNotUserModal(true);
            return;
        }
        if(!(phoneNum.first && phoneNum.middle && phoneNum.last)) {
            setAlertMessage("핸드폰 번호를 입력해주세요.");
            setShowNotUserModal(true);
            return;
        }

        if (!/^01[016789]$/.test(phoneNum.first) || !/^\d{3,4}$/.test(phoneNum.middle) || !/^\d{4}$/.test(phoneNum.last)) {
            setAlertMessage("올바른 휴대폰 번호를 입력해주세요.");
            setShowNotUserModal(true);
            return;
        }

        const revision = ++authRevision.current;
        setSendAuthState("sending");
        setAuthNum("");
        setIsValidAuth("none");
        setDisplayUserID("");
        try {
            const request = {
                displayName: displayName.trim(),
                phoneNumber: phoneNum.first + phoneNum.middle + phoneNum.last
            }
            const res = await authSMS(request);
            if (revision !== authRevision.current) return;
            if (parseInt(res.status/200, 10)===1 && res.data.message==="OK") {
                setSendAuthState("send");
            }else{
                setSendAuthState("fail");
            }
        }catch(err) {
            if (revision !== authRevision.current) return;
            console.error(err);
            setSendAuthState("fail");
        }
    }

    const handleCheckAuth = async () => {
        if (sendAuthState !== "send" || !/^\d{6}$/.test(authNum) || checkingAuth) {
            setAlertMessage("인증번호를 전송받은 후 6자리 숫자를 입력해주세요.");
            setShowNotUserModal(true);
            return;
        }
        const revision = authRevision.current;
        setCheckingAuth(true);

        try {
            const res = await checkAuthSMS({phoneNumber: phoneNum.first+phoneNum.middle+phoneNum.last, authNumber: authNum.trim()});
            if (revision !== authRevision.current) return;
            if(parseInt(res.status/200, 10)===1 && res.data.message==="OK") {
                setIsValidAuth("success");
            }else{
                setIsValidAuth("fail");
            }
        }catch(err) {
            if (revision === authRevision.current) setIsValidAuth("fail");
        }finally{
            if (revision === authRevision.current) setCheckingAuth(false);
        }
    }

    const handleSearchId = async () => {
        if(hasForbiddenChars(displayName) || hasForbiddenChars(phoneNum.first) || hasForbiddenChars(phoneNum.middle) || hasForbiddenChars(phoneNum.last) || hasForbiddenChars(authNum)) {
            return;
        }
        if (isValidAuth !== "success") {
            setAlertMessage("휴대폰 인증을 완료해주세요.");
            setShowNotUserModal(true);
            return;
        }
        // 모든 입력값은 필수입니다.
        if(!(displayName.trim() && phoneNum.first && phoneNum.middle && phoneNum.last)){
            setShowNotUserModal(true);
            return;
        }

        const revision = authRevision.current;
        try {
            const request = {
                displayName: displayName.trim(),
                phoneNumber: phoneNum.first+phoneNum.middle+phoneNum.last,
                authNumber: authNum.trim()
            }
            const res = await getUserID(request);
            if (revision !== authRevision.current) return;
            if(parseInt(res.status/200, 10)===1 && res.data.message==="OK"){
                setDisplayUserID(res.data.username);
            }else {
                throw new Error();
            }
        }catch(err){
            if (revision === authRevision.current) setShowNotUserModal(true);
        }

    }

    const handleMoveChagePw = async () => {
        if(hasForbiddenChars(id) || hasForbiddenChars(displayName) || hasForbiddenChars(phoneNum.first) || hasForbiddenChars(phoneNum.middle) || hasForbiddenChars(phoneNum.last) || hasForbiddenChars(authNum)) {
            return;
        }
        if (isValidAuth !== "success") {
            setAlertMessage("휴대폰 인증을 완료해주세요.");
            setShowNotUserModal(true);
            return;
        }
        // 모든 입력값은 필수입니다.
        if(!(id.trim() && displayName.trim() && phoneNum.first && phoneNum.middle && phoneNum.last)){
            setShowNotUserModal(true);
            return;
        }

        const revision = authRevision.current;
        try{
            const userInfo = {
                username: id.trim(),
                displayName: displayName.trim(),
                phoneNumber: phoneNum.first + phoneNum.middle + phoneNum.last,
                authNumber: authNum.trim()
            }
            const res = await checkExistUser(userInfo);
            if (revision !== authRevision.current) return;

            if(parseInt(res.status/200, 10)===1 && res.data.message==="OK"){        
                sessionStorage.setItem('userInfo',JSON.stringify(userInfo));
                navigate(`/change-pw/${type}/general`);
            }else {
                setShowNotUserModal(true);
            }
        }catch(err){
            if (revision === authRevision.current) setShowNotUserModal(true);
        }
    }


    // 비밀번호 찾기 실패 모달
    const NotUserModalHeader = (
		<div className='signinModal__header'>
			<p>아이디/비밀번호 찾기</p>
		</div>
	)

    useEffect(() => {
            if(!authNum) {
                setAlertMessage("인증코드를 입력해주세요.");
            }
            if(!phoneNum.middle || !phoneNum.last) {
                setAlertMessage("핸드폰 번호를 입력해주세요.");
            }
            if(!displayName) {
                setAlertMessage("이름을 입력해주세요.");
            }
        // 페이지가 로드될 때 초기화
        if(tab==='pw'){
            if(!id) {
                setAlertMessage("아이디를 입력해주세요.");
            }
        }
    }, [authNum, phoneNum, displayName, id, tab]); 
	const NotUserModalBody = (
		<div className='signinModal__body'>
			<p>{type==='dtx006' ? '등록된 회원이 아닙니다.' : alertMessage}</p>
			<button onClick={()=>setShowNotUserModal(false)}>확인</button>
		</div>
	)

    return (
        <div className="signinLayout" style={{ width: '100vw', height: '100vh' }}>
			<div className="signinLayout__wrapper">
				<div className="signinLayout__wrapper__header">
                    <span className="signinLayout__wrapper__header__title">아이디/비밀번호 찾기</span>
                </div>
                <div className="signinLayout__wrapper_tab">
                    <button
                        className={`${tab==='id' ? 'selected_tab' : ""}`}
                        onClick={()=>{
                            initializeInfo();
                            setTab('id');
                        }}
                    >
                        아이디 찾기
                    </button>
                    <button
                        className={`${tab==='pw' ? 'selected_tab' : ""}`}
                        onClick={()=>{
                            initializeInfo();
                            setTab('pw');
                        }}
                    >
                        비밀번호 찾기
                    </button>
                </div>
                {!displayUserID ? (
                    <div className={`signinLayout__wrapper__description center_black ${tab==="id" ? "colored_box" : ""}`}>
                        {tab==='id' && <p>회원정보에 등록된 핸드폰 번호를 통해 아이디를 찾습니다.</p>}
                        {tab==='pw' && type==='dtx006' && <p>회원정보에 등록된 아이디와 휴대폰 번호를 통해 비밀번호를 재설정합니다.</p>}
                        {tab==='pw' && type!=='dtx006' && <p>아이디와 회원정보에 등록된 핸드폰 번호를 통해 비밀번호를 재설정합니다.</p>}
                    </div>
                ) : (
                    <div className="signinLayout__wrapper__description center_black_big colored_box">
                        <p>{type==='dtx006' ? displayUserID : `아이디: ${displayUserID}`}</p>
                    </div>
                )}
                <div className="signinLayout__wrapper__body">
                    {tab==="pw" && <div
						className="signinLayout__wrapper__body__option"
                        style={{ width: '33rem', marginBottom: '2rem' }}
					>
						<span className="optionItem__label">아이디</span>
						<div className="optionItem__content">
							<div
                                 className='optionItem__content__input'
                                 style={{ width: '100%' }}
                            >
								<input
									type="text"
									value={id}
									onChange={e => {
                                        resetVerification();
                                        const raw = e.target.value;
                                        const filtered = raw.replace(/[^a-zA-Z0-9]/g, '');
                                        setId(filtered);
									}}
                                    maxLength={30}
                                    placeholder={type==='dtx006' ? '아이디를 입력해주세요.' : ''}
								/>
                                {hasForbiddenChars(id) && <p className="optionItem__content__input__validation non_valid">{FORBIDDEN_CHARS_MSG}</p>}
							</div>
						</div>
                    </div>}
                    <div
						className="signinLayout__wrapper__body__option"
						style={{ width: '33rem', marginBottom: '2rem' }}
					>
						<span className="optionItem__label">이름</span>
						<div className="optionItem__content">
							<div
                                 className="optionItem__content__input"
                                 style={{ width: '100%' }}
                            >
								<input
									type="text"
									value={displayName}
									onChange={e => {
                                        resetVerification();
                                        const raw = e.target.value;
                                        const filtered = raw.replace(/[^ㄱ-ㅎㅏ-ㅣ가-힣]/g, '');
                                        setDisplayUserID("");
										setDisplayName(filtered);
									}}
                                    maxLength={30}
                                    placeholder={type==='dtx006' ? '이름을 입력해주세요.' : ''}
								/>
                                {hasForbiddenChars(displayName) && <p className="optionItem__content__input__validation non_valid">{FORBIDDEN_CHARS_MSG}</p>}
							</div>
						</div>
                    </div>
                    <div
						className="signinLayout__wrapper__body__option"
						style={{ marginBottom: '2rem' }}
					>
						<span className="optionItem__label">{type==='dtx006' ? '휴대폰 번호' : '핸드폰 번호'}</span>
						<div className="optionItem__content" style={{ gap: '1rem' }}>
							<div
                                className='optionItem__content__input'
                                style={{ justifyContent: 'space-between' }}
                            >
                                {type==='dtx006' ? (
                                    <select
                                        className="optionItem__content__input__phone"
                                        inputMode="numeric"
                                        aria-label="휴대폰 번호 앞자리"
                                        value={phoneNum.first}
                                        onChange={e => {
                                            setDisplayUserID("");
                                            setSendAuthState("none");
                                            setAuthNum("");
                                            setIsValidAuth("none");
                                            resetVerification();
                                            setPhoneNum({ ...phoneNum, first: e.target.value });
                                        }}
                                    >
                                        <option value="010">010</option>
                                        <option value="011">011</option>
                                        <option value="016">016</option>
                                        <option value="017">017</option>
                                        <option value="018">018</option>
                                        <option value="019">019</option>
                                    </select>
                                ) : (
                                    <input
                                        type="text"
                                        className="optionItem__content__input__phone"
                                        inputMode="numeric"
                                        aria-label="휴대폰 번호 앞자리"
                                        value={phoneNum.first}
                                        onChange={e => {
                                            setDisplayUserID("");
                                            setSendAuthState("none");
                                            setAuthNum("");
                                            setIsValidAuth("none");
                                            resetVerification();
                                            setPhoneNum({...phoneNum, first: e.target.value.replace(/\D/g, '').slice(0, 3)});
                                        }}
                                        maxLength={3}
                                    />
                                )}
                                <div className="optionItem__content__input__phone__divider">ㅡ</div>
                                <input
									type="text"
                                    className="optionItem__content__input__phone"
                                    inputMode="numeric"
                                    aria-label="휴대폰 번호 중간자리"
									value={phoneNum.middle}
									onChange={e => {
                                        resetVerification();
                                        const {value} = e.target;
                                        const digits = value.replace(/\D/g, '').slice(0, 4);
                                        setPhoneNum({ ...phoneNum, middle: digits });
                                        setDisplayUserID("");
                                        if (type === 'dtx006' && digits.length === 4) {
                                            lastPhoneNumInputRef.current?.focus();
                                        }
                                        setSendAuthState("none"); 
                                        setAuthNum("");    
                                        setIsValidAuth("none");
									}}
                                    maxLength={4}
								/>
                                <div className="optionItem__content__input__phone__divider">ㅡ</div>
                                <input
                                    ref={lastPhoneNumInputRef}
									type="text"
                                    className="optionItem__content__input__phone"
                                    inputMode="numeric"
                                    aria-label="휴대폰 번호 끝자리"
									value={phoneNum.last}
									onChange={e => {
                                        resetVerification();
                                        const {value} = e.target;
                                        setPhoneNum({ ...phoneNum, last: value.replace(/\D/g, '').slice(0, 4) });
                                        setDisplayUserID("");
                                        setSendAuthState("none");
                                        setAuthNum("");
                                        setIsValidAuth("none");
									}}
                                    maxLength={4}
								/>
                                {sendAuthState==="send" && <p className="optionItem__content__input__validation valid">인증번호를 전송했습니다.</p>}
                                {sendAuthState==="fail" && <p className="optionItem__content__input__validation non_valid">존재하지 않는 계정입니다.</p>}
                                {(hasForbiddenChars(phoneNum.first) || hasForbiddenChars(phoneNum.middle) || hasForbiddenChars(phoneNum.last)) && <p className="optionItem__content__input__validation non_valid">{FORBIDDEN_CHARS_MSG}</p>}
							</div>
                            <button
                                className="optionItem__content__input__button"
                                onClick={handleSendAuthSMS}
                                disabled={sendAuthState === "sending"}
                            >
                                인증번호 전송
                            </button>
						</div>
                    </div>
                    <div
						className="signinLayout__wrapper__body__option"
						style={{ marginBottom: '4.375rem' }}
					>
						<span className="optionItem__label">인증번호</span>
						<div className="optionItem__content" style={{ gap: '1rem' }}>
							<div
                                 className={`optionItem__content__input ${sendAuthState==="send" && isValidAuth==="fail" ? "non_valid_input" : ""}`}
                            >
								<input
									type="text"
									value={authNum}
									onChange={e => {
                                        setIsValidAuth("none");
										authRevision.current += 1;
                                        setCheckingAuth(false);
                                        setDisplayUserID("");
                                        setAuthNum(e.target.value.replace(/\D/g, '').slice(0, 6));
									}}
                                    disabled={sendAuthState!=="send"}
                                    placeholder={type==='dtx006' ? '인증번호를 입력해주세요.' : ''}
                                    maxLength={6}
                                    inputMode="numeric"
                                    aria-label="인증번호"
								/>          
                                {sendAuthState==="send" && isValidAuth==="success" && <p className="optionItem__content__input__validation valid">인증번호가 일치합니다.</p>}
                                {sendAuthState==="send" && isValidAuth==="fail" && <p className="optionItem__content__input__validation non_valid">인증번호가 일치하지 않습니다.</p>}
                                {hasForbiddenChars(authNum) && <p className="optionItem__content__input__validation non_valid">{FORBIDDEN_CHARS_MSG}</p>}
							</div>
                            <button
                                className="optionItem__content__input__button"
                                onClick={handleCheckAuth}
                                disabled={sendAuthState !== "send" || !/^\d{6}$/.test(authNum) || checkingAuth}
                            >
                                {type==="dtx006" ? "인증하기" : "확인"}
                            </button>
						</div>
                    </div>
                    <div className="signinLayout__wrapper__body__footer multiple">
						<button
							className="submitButton"
                            onClick={tab==="id" ? handleSearchId : handleMoveChagePw}
                            disabled={isValidAuth !== "success"}
						>
							{tab==="id" ? '아이디 찾기' : '비밀번호 재설정'}
						</button>
                        <button
							className="cancelButton"
                            onClick={()=>navigate(`/sign-in/${type}`)}
						>
                            로그인 화면
						</button>
					</div>
                </div>
            </div>
            <CommonModal
				size="sm"
				header={NotUserModalHeader}
				body={NotUserModalBody}
				show={showNotUserModal}
				setClose={()=>setShowNotUserModal(false)}
			/>
        </div>
    )
}

export default SearchAccount;
